import { Component, OnInit, OnDestroy, inject, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { State } from '../shared/core/state';
import * as UserActions from '../shared/core/state/users/user.actions';
import {
  selectSelectedTutorProfile,
  selectTutorProfileLoading,
  selectTutorProfileError,
} from '../shared/core/state/users/user.selectors';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  Meta,
  Title,
  SafeResourceUrl,
  DomSanitizer,
} from '@angular/platform-browser';
import { getResourcePath } from '../shared/util/helper.utils';
import { TenantService } from '../shared/services/tenant.service';

@Component({
  selector: 'black-tutor-profile-view',
  templateUrl: './tutor-profile-view.component.html',
  styleUrls: ['./tutor-profile-view.component.css'],
  standalone: false,
})
export class TutorProfileViewComponent implements OnInit, OnDestroy {
  private document = inject<Document>(DOCUMENT);

  private store = inject<Store<State>>(Store);
  private route = inject(ActivatedRoute);
  private meta = inject(Meta);
  private title = inject(Title);
  private renderer = inject(Renderer2);
  private sanitizer = inject(DomSanitizer);
  private tenantService = inject(TenantService);
  private sub = new Subscription();

  tutor: any = null;
  courses: any[] = [];
  liveClasses: any[] = [];
  loading = false;
  error: string | null = null;

  youtubeEmbedUrl: SafeResourceUrl | null = null;
  private jsonLdScript: HTMLScriptElement | null = null;

  ngOnInit(): void {
    const identifier = this.route.snapshot.paramMap.get('identifier');
    if (identifier) {
      this.store.dispatch(UserActions.loadTutorProfile({ identifier }));
    }

    this.sub.add(
      this.store.select(selectSelectedTutorProfile).subscribe((data) => {
        if (data) {
          this.tutor = data.tutor;
          this.courses = data.courses || [];
          this.liveClasses = data.liveClasses || [];

          this.setSEOAndJSONLD();
          this.setYoutubeEmbed();
        }
      }),
    );

    this.sub.add(
      this.store.select(selectTutorProfileLoading).subscribe((loading) => {
        this.loading = loading;
      }),
    );

    this.sub.add(
      this.store.select(selectTutorProfileError).subscribe((error) => {
        this.error = error;
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.jsonLdScript) {
      this.renderer.removeChild(this.document.head, this.jsonLdScript);
    }
    this.tenantService.updateDocumentTitle();
  }

  setSEOAndJSONLD() {
    if (!this.tutor) return;
    const name = `${this.tutor.profile?.personal?.firstName} ${this.tutor.profile?.personal?.lastName}`;
    const jobTitle =
      this.tutor.profile?.professional?.jobTitle || 'Expert Tutor';
    const bio = this.tutor.profile?.personal?.biography || '';
    const avatarUrl = this.getAvatarUrl(this.tutor.profile?.personal?.avatar);

    // Set page Title
    const brand = this.tenantService.getTenantBrandName();
    const fullTitle = `${name} - ${jobTitle} | ${brand}`;
    this.title.setTitle(fullTitle);

    // Set Meta tags
    this.meta.updateTag({
      name: 'description',
      content: `Find ${name}, ${jobTitle} on ${brand}. Direct WhatsApp booking for physical and online classes.`,
    });
    this.meta.updateTag({
      property: 'og:title',
      content: fullTitle,
    });
    this.meta.updateTag({
      property: 'og:description',
      content: `Find ${name}, ${jobTitle} on ${brand}. Contact via WhatsApp for home visits, group tuition, and online classes.`,
    });
    this.meta.updateTag({ property: 'og:image', content: avatarUrl });

    // Inject JSON-LD
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: name,
      jobTitle: jobTitle,
      description: bio,
      image: avatarUrl,
      address: {
        '@type': 'PostalAddress',
        addressLocality: this.tutor.profile?.address?.city || '',
        addressRegion: this.tutor.profile?.address?.province || '',
        addressCountry: 'Sri Lanka',
      },
      knowsAbout: this.tutor.profile?.tutorInfo?.subjects || [],
    };

    if (this.jsonLdScript) {
      this.renderer.removeChild(this.document.head, this.jsonLdScript);
    }

    this.jsonLdScript = this.renderer.createElement('script');
    this.renderer.setAttribute(
      this.jsonLdScript,
      'type',
      'application/ld+json',
    );
    this.jsonLdScript!.text = JSON.stringify(jsonLd);
    this.renderer.appendChild(this.document.head, this.jsonLdScript);
  }

  setYoutubeEmbed() {
    const videoUrl = this.tutor?.profile?.tutorInfo?.youtubeChannel;
    if (!videoUrl) {
      this.youtubeEmbedUrl = null;
      return;
    }

    let videoId = '';
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = videoUrl.match(regExp);
    if (match && match[2].length === 11) {
      videoId = match[2];
    }

    if (videoId) {
      this.youtubeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${videoId}`,
      );
    } else {
      this.youtubeEmbedUrl = null;
    }
  }

  getAvatarUrl(avatar: any): string {
    if (!avatar) return 'assets/img/default-avatar.png';
    return getResourcePath(avatar);
  }

  getBannerUrl(banner: any): string {
    if (!banner) return 'assets/img/default-tutor-banner.jpg';
    return getResourcePath(banner);
  }

  getWhatsAppLink(classTitle?: string, city?: string, venue?: string): string {
    const whatsAppNum =
      this.tutor?.profile?.tutorInfo?.whatsAppNumber ||
      this.tutor?.mobile ||
      '';
    if (!whatsAppNum) return '#';

    let sanitizedNum = whatsAppNum.replace(/[^0-9]/g, '');
    if (sanitizedNum.startsWith('0')) {
      sanitizedNum = '94' + sanitizedNum.substring(1);
    } else if (!sanitizedNum.startsWith('94') && sanitizedNum.length === 9) {
      sanitizedNum = '94' + sanitizedNum;
    }

    const tutorName = `${this.tutor.profile?.personal?.firstName} ${this.tutor.profile?.personal?.lastName}`;

    let text = `Hi ${tutorName}, I saw your profile on Learnbird and wanted to make an inquiry.`;
    if (classTitle) {
      text = `Hi ${tutorName}, I saw your profile on Learnbird and would like to enroll in your physical class "${classTitle}"`;
      if (venue || city) {
        text += ` at ${venue || city}`;
      }
      text += `. Please let me know how to proceed.`;
    }

    return `https://wa.me/${sanitizedNum}?text=${encodeURIComponent(text)}`;
  }
}
